import React, { useContext, useEffect, useState } from 'react';
import { Alert, Dialog, Loader, ThemePrepared } from '@fluentui/react-northstar';
import { useTranslation } from 'react-i18next';
import { Ticket } from '../../interfaces/ticket.interface';
import { platformService } from '../../services/platform.service';
import { ListContext, ListContextType } from '../../../tikit/List/ListContextProvider';
import { CustomViewContext } from '../../../tikit/toolbar/CustomViewContextProvider';
import { Toast } from '../toast/Toast';

interface Props {
  globalTheme: ThemePrepared<any>;
  open: boolean;
  onClose: (value: boolean) => void;
  tickets: Ticket[];
  onSuccessfullyDeleted: (listContext: ListContextType, customViewStates: CustomViewStatesType) => void;
}

const api = new platformService();

export const BulkDeleteConfirmDialog = ({
  open,
  onClose,
  tickets,
  onSuccessfullyDeleted,
}: Props) => {
  const { t } = useTranslation();
  const { listStates, setListStates } = useContext(ListContext);
  const { customViewStates } = useContext(CustomViewContext);

  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setError('');
      setIsDeleting(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    setError('');
    setIsDeleting(true);
    try {
      const ids = tickets.map((t) => t.Id);
      await api.batchDeleteTickets(ids);
      Toast.success(t('ticket.ticket-list.bulk-delete.success', { count: ids.length }));
      onSuccessfullyDeleted({ listStates, setListStates }, customViewStates);
      onClose(false);
    } catch {
      setError(t('ticket.ticket-list.bulk-delete.error'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      style={{ width: 480 }}
      closeOnOutsideClick={false}
      open={open}
      header={t('ticket.ticket-list.bulk-delete.header')}
      cancelButton={{
        content: t('common.buttons.cancel'),
        styles: isDeleting ? { display: 'none' } : {},
      }}
      confirmButton={{
        content: t('common.buttons.delete'),
        primary: true,
        styles: isDeleting ? { display: 'none' } : {},
      }}
      onConfirm={handleConfirm}
      onCancel={() => onClose(false)}
      content={
        <div>
          {error && <Alert content={error} danger visible styles={{ marginBottom: '0.75rem' }} />}
          {isDeleting ? (
            <Loader label={t('ticket.ticket-list.bulk-delete.deleting')} />
          ) : (
            <p style={{ margin: 0 }}>
              {t('ticket.ticket-list.bulk-delete.confirm-message', { count: tickets.length })}
            </p>
          )}
        </div>
      }
    />
  );
};
